import { CommuneFields } from './CommuneFields';
import { currentPrice, guessTerritory, uuidv7, type CatalogItemRecord } from '@clim/domain';
import { clientName } from '@clim/client-core';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { ErrorBox, Field, Input, Modal, Select, Textarea } from './ui';

const NEW = '__new__';
const power = (i: CatalogItemRecord) =>
  i.powerMinBtu == null ? null : `${i.powerMinBtu.toLocaleString('fr-FR')} BTU`;

/**
 * Intervention pour un partenaire (sous-traitance de pose), saisie en une fois depuis le planning : partenaire,
 * client, prestation (puissance → rémunération du poseur), rendez-vous et technicien. Pas de dossier de vente à
 * remplir : le serveur crée le client du partenaire, le chantier « À planifier » et l'intervention.
 */
export function PartnerJobDialog({ onClose }: { onClose(): void }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const { territoryId: headerTerritory } = useTerritory();
  const partners = engine
    .list('reason')
    .filter((r) => r.category === 'partner' && r.isActive)
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  const territories = engine
    .list('territory')
    .filter((t) => t.isActive && (me.permissions['dossier.create'] === 'all' || me.territoryIds.includes(t.id)))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const types = engine.list('dossier_type').filter((t) => t.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const [f, setF] = useState({
    partnerId: partners[0]?.id ?? NEW,
    partnerName: '',
    kind: 'individual' as 'individual' | 'company',
    companyName: '',
    contactName: '',
    siret: '',
    lastName: '',
    firstName: '',
    phone: '',
    addressLine1: '',
    postalCode: '',
    city: '',
    territoryId: '',
    dossierTypeId: types.find((t) => t.code === 'installation')?.id ?? types[0]?.id ?? '',
    partnerReference: '',
    atHeight: null as boolean | null,
    access: 'ladder' as 'ladder' | 'nacelle',
    heightM: '',
    scheduledDate: '',
    slotStart: '',
    slotEnd: '',
    technicianId: '',
    notes: '',
  });
  const [lines, setLines] = useState<{ itemId: string; quantity: number }[]>([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<{ dossierId: string } | null>(null);
  // client déjà enregistré (le nôtre ou celui d'un partenaire) : choisi dans la base, rien à ressaisir
  const [existingId, setExistingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const existing = existingId ? engine.get('client', existingId) : undefined;
  const found = useMemo(() => {
    const q = search.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    if (q.length < 2) return [];
    return engine
      .list('client')
      .filter(
        (c) =>
          clientName(c).toLowerCase().includes(q) ||
          (digits.length >= 4 && (c.phone ?? '').replace(/\D/g, '').includes(digits)) ||
          (c.city ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [engine, engine.version, search]);
  const territoryId = existing?.territoryId ?? (f.territoryId || guessTerritory(territories, f.postalCode, headerTerritory) || '');
  const [techs, setTechs] = useState<{ id: string; name: string; company?: string | null }[]>([]);
  const [helpers, setHelpers] = useState<string[]>([]);
  useEffect(() => {
    if (!territoryId) return setTechs([]);
    api
      .request<{ id: string; name: string }[]>('GET', `/v1/planning/technicians?territoryId=${territoryId}`)
      .then(setTechs, () => setTechs([]));
  }, [api, territoryId]);
  // prestations : tout article avec un tarif sur ce territoire (même une variante pas encore ouverte à la vente),
  // aucun prix affiché — seule compte la rémunération de pose
  const items = useMemo(
    () =>
      engine
        .list('catalog_item')
        .filter((i) => !i.dossierTypeIds.length || i.dossierTypeIds.includes(f.dossierTypeId))
        .filter((i) => !territoryId || !i.territoryIds.length || i.territoryIds.includes(territoryId))
        .filter((i) => !territoryId || currentPrice(i.prices, territoryId))
        .sort((a, b) => a.sortOrder - b.sortOrder || (a.powerMinBtu ?? 0) - (b.powerMinBtu ?? 0)),
    [engine, engine.version, f.dossierTypeId, territoryId],
  );
  const sections = engine.list('catalog_section');
  const itemLabel = (i: CatalogItemRecord | undefined) =>
    i
      ? [i.label, i.brandId ? engine.get('catalog_brand', i.brandId)?.label : null, power(i) && !i.label.includes('BTU') ? power(i) : null]
          .filter(Boolean)
          .join(' – ')
      : '?';
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch });

  const submit = async () => {
    setError(null);
    setErrors({});
    if (!territoryId) return setError('Indiquez le code postal du client (ou le territoire)');
    setBusy(true);
    try {
      const dossierId = uuidv7();
      await api.request('POST', '/v1/partner-jobs', {
        clientId: existing?.id ?? uuidv7(),
        existingClient: !!existing,
        dossierId,
        interventionId: uuidv7(),
        partnerId: f.partnerId === NEW ? null : f.partnerId,
        partnerName: f.partnerId === NEW ? f.partnerName : null,
        territoryId,
        client: {
          kind: f.kind,
          lastName: f.lastName || null,
          firstName: f.firstName || null,
          companyName: f.kind === 'company' ? f.companyName || null : null,
          contactName: f.kind === 'company' ? f.contactName || null : null,
          siret: f.kind === 'company' ? f.siret.replace(/\s/g, '') || null : null,
          phone: f.phone || null,
          addressLine1: f.addressLine1 || null,
          postalCode: f.postalCode || null,
          city: f.city || null,
        },
        dossierTypeId: f.dossierTypeId,
        partnerReference: f.partnerReference || null,
        installAtHeight: f.atHeight,
        installAccess: f.atHeight ? f.access : null,
        installHeightM: f.atHeight && f.heightM ? Number(f.heightM.replace(',', '.')) : null,
        lines,
        scheduledDate: f.scheduledDate || null,
        slotStart: f.slotStart || null,
        slotEnd: f.slotEnd || null,
        technicianId: f.technicianId || null,
        coTechnicianIds: f.technicianId ? helpers.filter((x) => x !== f.technicianId) : [],
        notes: f.notes || null,
      });
      void engine.sync();
      setDone({ dossierId });
    } catch (e) {
      setErrors(fieldErrors(e));
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <Modal title="Intervention partenaire" onClose={onClose}>
        <div className="form">
          <p className="success-text" role="status">
            Intervention enregistrée{f.technicianId ? ' et proposée au technicien' : ''}.
          </p>
          <p>Vous pouvez ajouter la photo du bon du partenaire et d’autres photos sur la fiche du chantier.</p>
          <div className="actions">
            <Link className="btn" to={`/dossiers/${done.dossierId}`}>
              Ajouter la photo du bon
            </Link>
            <button className="btn primary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </Modal>
    );

  return (
    <Modal title="Intervention partenaire" onClose={onClose}>
      <div className="form">
        <p className="muted">
          Sous-traitance de pose : pas de vente (ni prix, ni signature, ni paiement). Le technicien est rémunéré selon
          le barème de la prestation choisie ; ces rémunérations portent le nom du partenaire.
        </p>
        <div className="grid2">
          <Field label="Partenaire" error={errors.partnerName}>
            <Select aria-label="Partenaire" value={f.partnerId} onChange={(e) => set({ partnerId: e.target.value })}>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value={NEW}>+ Nouveau partenaire…</option>
            </Select>
          </Field>
          {f.partnerId === NEW ? (
            <Field label="Nom du partenaire" error={errors.partnerName}>
              <Input value={f.partnerName} onChange={(e) => set({ partnerName: e.target.value })} />
            </Field>
          ) : (
            <Field label="N° du bon du partenaire (facultatif)">
              <Input value={f.partnerReference} onChange={(e) => set({ partnerReference: e.target.value })} />
            </Field>
          )}
        </div>
        {f.partnerId === NEW && (
          <Field label="N° du bon du partenaire (facultatif)">
            <Input value={f.partnerReference} onChange={(e) => set({ partnerReference: e.target.value })} />
          </Field>
        )}

        <h3>Client</h3>
        {existing ? (
          <div className="card existing-client" data-testid="existing-client">
            <strong>{clientName(existing)}</strong>
            <span className="muted">
              {[existing.phone, existing.addressLine1, [existing.postalCode, existing.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
            </span>
            {existing.partnerId && <span className="badge partner">Client de : {engine.get('reason', existing.partnerId)?.label ?? 'partenaire'}</span>}
            <button type="button" className="btn small" onClick={() => setExistingId(null)}>
              Changer de client
            </button>
          </div>
        ) : (
          <>
            <Field label="Client déjà enregistré ? Recherchez-le (nom, téléphone, commune)">
              <Input type="search" aria-label="Rechercher un client existant" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Field>
            {found.length > 0 && (
              <ul className="list plain">
                {found.map((c) => (
                  <li key={c.id}>
                    <button type="button" className="row" onClick={() => (setExistingId(c.id), setSearch(''))}>
                      <span className="row-main">
                        <strong>{clientName(c)}</strong>
                        <span className="muted">{[c.phone, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className="btn small">Choisir</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {search.trim().length >= 2 && !found.length && <p className="muted small">Aucun client trouvé : saisissez-le ci-dessous.</p>}
            <p className="muted small">Sinon, nouveau client :</p>
          </>
        )}
        {!existing && (
        <>
        <div className="segmented" role="radiogroup" aria-label="Type de client">
          {(['individual', 'company'] as const).map((k) => (
            <button type="button" key={k} className={f.kind === k ? 'on' : ''} aria-pressed={f.kind === k} onClick={() => set({ kind: k })}>
              {k === 'individual' ? 'Particulier' : 'Professionnel'}
            </button>
          ))}
        </div>
        {f.kind === 'company' && (
          <div className="grid2">
            <Field label="Raison sociale" error={errors.companyName}>
              <Input value={f.companyName} onChange={(e) => set({ companyName: e.target.value })} />
            </Field>
            <Field label="SIRET (facultatif)" error={errors.siret}>
              <Input inputMode="numeric" value={f.siret} onChange={(e) => set({ siret: e.target.value })} />
            </Field>
            <Field label="Personne à contacter">
              <Input value={f.contactName} onChange={(e) => set({ contactName: e.target.value })} />
            </Field>
          </div>
        )}
        <div className="grid2">
          <Field label={f.kind === 'company' ? 'Nom (facultatif)' : 'Nom du client'} error={errors.lastName}>
            <Input value={f.lastName} onChange={(e) => set({ lastName: e.target.value })} />
          </Field>
          <Field label="Prénom">
            <Input value={f.firstName} onChange={(e) => set({ firstName: e.target.value })} />
          </Field>
          <Field label="Téléphone du client" error={errors.phone}>
            <Input type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="Adresse d’intervention">
            <Input value={f.addressLine1} onChange={(e) => set({ addressLine1: e.target.value })} />
          </Field>
          <CommuneFields
            postalCode={f.postalCode}
            city={f.city}
            errors={{ postalCode: errors.postalCode ?? errors['client.postalCode'], city: errors.city ?? errors['client.city'] }}
            onChange={(v) => set(v)}
          />
        </div>
        </>
        )}
        {!existing && territories.length > 1 && !guessTerritory(territories, f.postalCode, headerTerritory) && (
          <Field label="Territoire">
            <Select value={f.territoryId} onChange={(e) => set({ territoryId: e.target.value })}>
              <option value="">Choisir…</option>
              {territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <h3>Prestation</h3>
        <Field label="Type">
          <Select value={f.dossierTypeId} onChange={(e) => set({ dossierTypeId: e.target.value })}>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        <div className={`field${errors.installAtHeight ? ' has-error' : ''}`}>
          <span className="field-label" id="pj-height">
            Intervention en hauteur ?
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="pj-height">
            {(
              [
                [false, 'Non'],
                [true, 'Oui'],
              ] as const
            ).map(([v, l]) => (
              <button
                type="button"
                key={l}
                role="radio"
                aria-checked={f.atHeight === v}
                className={f.atHeight === v ? 'on' : ''}
                onClick={() => set({ atHeight: v })}
              >
                {l}
              </button>
            ))}
          </div>
          {errors.installAtHeight && <span className="field-error">{errors.installAtHeight}</span>}
        </div>
        {f.atHeight && (
          <div className="grid2">
            <Field label="Accès">
              <Select value={f.access} onChange={(e) => set({ access: e.target.value as 'ladder' | 'nacelle' })}>
                <option value="ladder">Échelle</option>
                <option value="nacelle">Nacelle</option>
              </Select>
            </Field>
            <Field label="Hauteur approximative (m)">
              <Input inputMode="decimal" value={f.heightM} onChange={(e) => set({ heightM: e.target.value })} />
            </Field>
          </div>
        )}
        <Field label="Puissance / prestation à poser" error={errors.lines} hint="la rémunération du poseur suit le barème du catalogue">
          <Select
            aria-label="Puissance / prestation à poser"
            value={pick}
            onChange={(e) => {
              const id = e.target.value;
              setPick('');
              if (id) setLines(lines.some((l) => l.itemId === id) ? lines : [...lines, { itemId: id, quantity: 1 }]);
            }}
          >
            <option value="">+ Choisir dans le catalogue…</option>
            {sections
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((s) => {
                const list = items.filter((i) => i.sectionId === s.id);
                return list.length ? (
                  <optgroup key={s.id} label={s.label}>
                    {list.map((i) => (
                      <option key={i.id} value={i.id}>
                        {itemLabel(i)}
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
          </Select>
        </Field>
        {lines.length > 0 && (
          <ul className="plain">
            {lines.map((l, k) => (
              <li key={l.itemId} className="row">
                <span className="row-main">{itemLabel(engine.get('catalog_item', l.itemId))}</span>
                <Input
                  type="number"
                  min={1}
                  aria-label={`Quantité ${itemLabel(engine.get('catalog_item', l.itemId))}`}
                  value={l.quantity}
                  style={{ width: 70 }}
                  onChange={(e) => setLines(lines.map((x, j) => (j === k ? { ...x, quantity: Math.max(1, Number(e.target.value) || 1) } : x)))}
                />
                <button type="button" className="btn small ghost" aria-label="Retirer" onClick={() => setLines(lines.filter((_, j) => j !== k))}>
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <h3>Rendez-vous</h3>
        <div className="grid3">
          <Field label="Date">
            <Input type="date" value={f.scheduledDate} onChange={(e) => set({ scheduledDate: e.target.value })} />
          </Field>
          <Field label="Début">
            <Input type="time" value={f.slotStart} onChange={(e) => set({ slotStart: e.target.value })} />
          </Field>
          <Field label="Fin">
            <Input type="time" value={f.slotEnd} onChange={(e) => set({ slotEnd: e.target.value })} />
          </Field>
        </div>
        <Field label="Technicien" hint="facultatif : l’intervention lui est proposée tout de suite">
          <Select aria-label="Technicien" value={f.technicianId} onChange={(e) => set({ technicianId: e.target.value })}>
            <option value="">À attribuer plus tard</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.company ? ` (${t.company})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        {f.technicianId && techs.length > 1 && (
          <fieldset className="crew" data-testid="crew-new">
            <legend>Renfort (intervention à plusieurs, facultatif)</legend>
            {techs
              .filter((t) => t.id !== f.technicianId)
              .map((t) => (
                <label key={t.id} className="check">
                  <input
                    type="checkbox"
                    checked={helpers.includes(t.id)}
                    onChange={(e) => setHelpers(e.target.checked ? [...helpers, t.id] : helpers.filter((x) => x !== t.id))}
                  />{' '}
                  {t.name}
                  {t.company && <span className="muted"> ({t.company})</span>}
                </label>
              ))}
            <p className="muted small">La rémunération de pose sera à répartir entre eux au moment de la valider.</p>
          </fieldset>
        )}
        <Field label="Consignes d’intervention (facultatif)">
          <Textarea value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" disabled={busy} onClick={() => void submit()}>
            Enregistrer l’intervention
          </button>
        </div>
      </div>
    </Modal>
  );
}
